import React, { useEffect, useContext, useState, useMemo, useRef } from 'react'
import { ScrollView, View, StyleSheet, FlatList } from 'react-native'
import { Text, Spinner, Card, Icon, Button, Layout, Modal, Input, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { findClientByPhone, getClientById, deleteClient, getClientOperations, updateClient, getClientSalesPaginated, getClientSalesSummary } from '../../global/Firebase/firebase'
import { normalizePhoneText } from '../../utils/phoneNumber'
import { format, differenceInCalendarDays } from 'date-fns'
import { BonusValueChange } from '../../components/BonusValueChange'
import { FontAwesome5 } from '@expo/vector-icons'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { constantsAtom, currencyFormatterAtom, storesDictAtom, userRoleAtom, usersDictAtom, discountOptionsListAtom, priceTypesAtom } from '../../global/AtomsProvider'
import { useToast } from "react-native-toast-notifications"
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { CheckBox } from '@ui-kitten/components'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { resolveBonusLevel, isValidBonusLevelInput } from '../../utils/bonusLevel'
import { WhatsAppButton } from '../../components/WhatsAppButton'

/**
 * Backend (Supabase) clients expose `name`/`phone`; Firestore uses `displayName`/`phoneNumber`.
 * A copy with both, so the screen reads one shape — never a mutation of the route's own object.
 */
const normalizeClientShape = (value) => {
  if (!value) return value
  return {
    ...value,
    displayName: value.displayName ?? value.name,
    phoneNumber: value.phoneNumber ?? value.phone,
  }
}

export default function ClientDetailScreen ({ navigation, route }) {
  const theme = useTheme()
  const requiresUpdate = route.params?.requiresUpdate
  const { t } = useTranslation()
  // ATL-1513: the client this card shows, held in STATE. It starts as the route's snapshot and is
  // replaced by the stored client once resolved — reassigning a local (`client = updatedClient`, as
  // this screen used to) never re-rendered the header, so it kept showing the snapshot while the lists
  // below could belong to another client.
  const routeClient = useMemo(
    () => normalizeClientShape(route.params?.client || route.params?.detail),
    [route.params?.client, route.params?.detail]
  )
  const [client, setClient] = useState(routeClient)
  // 'missing' — no stored client has the snapshot's id any more (deleted); 'failed' — it could not be
  // re-read. The card then shows the snapshot, SAYS which, and still loads the history by the id.
  const [clientLookupProblem, setClientLookupProblem] = useState(null)
  // True once the card holds the STORED client. Until then (and when it could not be re-read) the card
  // shows a snapshot — a sale's `{id, displayName, phoneNumber}`, or a search hit — so editing or
  // changing the bonus from it would write the snapshot's gaps back over the stored client (a cleared
  // discount, price tier, address) or start a bonus change from a stale balance.
  const [clientResolved, setClientResolved] = useState(false)
  const [context] = useContext(UserContext)
  const db = context.db
  const usersDict = useAtomValue(usersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const originalDiscountOptions = useAtomValue(discountOptionsListAtom)
  const discountOptions = useMemo(() => {
    return originalDiscountOptions.filter(option => option.percentage != null)
  }, [originalDiscountOptions])
  
  const priceTypes = useAtomValue(priceTypesAtom)
  const [purchasesInfo, setPurchasesInfo] = useState(null)
  const [clientOperations, setClientOperations] = useState(null)
  const [showBonusChangeModal, setShowBonusChangeModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editedClient, setEditedClient] = useState(null)
  const [editError, setEditError] = useState(null)
  const [salesData, setSalesData] = useState([])
  const [lastSalesDocument, setLastSalesDocument] = useState(null)
  // Starts true: until the first page answers, "no purchases" would be a false statement.
  const [isLoadingSales, setIsLoadingSales] = useState(true)
  const [isLoadingMoreSales, setIsLoadingMoreSales] = useState(false)
  const [hasMoreSales, setHasMoreSales] = useState(true)
  // ATL-1472: every loader ends in data OR an explicit error, never an endless spinner. Each section
  // loads on its own, so one failing request (the operations read 404'd for every client) cannot
  // hide the others.
  const [purchasesInfoError, setPurchasesInfoError] = useState(false)
  const [clientOperationsError, setClientOperationsError] = useState(false)
  const [salesError, setSalesError] = useState(false)
  // The id every section was loaded for — load-more must page the SAME client.
  const [resolvedClientId, setResolvedClientId] = useState(null)
  // Synchronous in-flight flag for load-more: FlatList can fire onEndReached twice before the
  // `isLoadingMoreSales` state re-renders, and both calls would read the same cursor and append the
  // same page twice.
  const loadingMoreSalesRef = useRef(false)
  // ATL-1513: every load belongs to one client. When the route's client changes, answers still in
  // flight for the previous one must not land on this card — each load checks it is still current.
  const generationRef = useRef(0)
  const toast = useToast()

  useEffect(() => {
    if (!routeClient) return
    const generation = ++generationRef.current
    const isCurrent = () => generationRef.current === generation

    // Nothing of the previous client may survive onto this one — including the list's loading and
    // paging state (a card reached again from a sale must not say "no purchases" while it resolves).
    setClient(routeClient)
    setClientResolved(false)
    setClientLookupProblem(null)
    setIsLoadingSales(true)
    setIsLoadingMoreSales(false)
    setLastSalesDocument(null)
    setHasMoreSales(true)
    loadingMoreSalesRef.current = false
    setPurchasesInfo(null)
    setPurchasesInfoError(false)
    setClientOperations(null)
    setClientOperationsError(false)
    setSalesData([])
    setSalesError(false)
    setResolvedClientId(null)

    // Log client detail data for E2E testing
    console.log('[CLIENT_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
      id: routeClient.id,
      displayName: routeClient.displayName,
      phoneNumber: routeClient.phoneNumber,
      organizationId: context.user?.organizationId,
      bonusPoints: routeClient.bonusPoints,
      bonusLevel: routeClient.bonusLevel,
      loyaltyDiscount: routeClient.loyaltyDiscount,
      address: routeClient.address,
    })))

    const resolveAndLoad = async () => {
      let resolved = routeClient
      // Whatever opened the card — a sale's snapshot (SaleDetail) or a search hit — carries only part
      // of the client: re-read the client it NAMES, by id. The phone is only a fallback for a sale's
      // snapshot with no id: a phone is not an identity (duplicate phones exist, and a lookup by one
      // could open another client — ATL-1513, ATL-1523).
      const lookup = routeClient.id
        ? () => getClientById(db, routeClient.id)
        : requiresUpdate && routeClient.phoneNumber
          ? () => findClientByPhone(db, routeClient.phoneNumber)
          : null
      if (lookup) {
        try {
          const stored = await lookup()
          if (!isCurrent()) return
          if (stored) {
            resolved = normalizeClientShape(stored)
            setClientResolved(true)
          } else {
            setClientLookupProblem('missing')
          }
        } catch (error) {
          if (!isCurrent()) return
          console.error('Error re-reading the client:', error)
          Sentry.captureException(error)
          setClientLookupProblem('failed')
        }
      }
      if (!isCurrent()) return
      setClient(resolved)

      if (!resolved.id) {
        // Nothing can be loaded without an id: say so rather than spin.
        setIsLoadingSales(false)
        setSalesError(true)
        setPurchasesInfoError(true)
        return
      }
      setResolvedClientId(resolved.id)
      // Each loader ends in data or an explicit error of its own; nothing to await here.
      void loadClientSummary(resolved.id, isCurrent)
      void loadClientSales(resolved.id, true, isCurrent)
    }

    void resolveAndLoad()
  }, [routeClient])

  // The edit form follows the client the card shows — not the route snapshot it started from (saving
  // a form filled from a stale snapshot would write the stale values back over the stored client).
  useEffect(() => {
    if (client && !showEditModal) {
      setEditedClient({
        displayName: client.displayName,
        phoneNumber: client.phoneNumber,
        address: client.address || '',
        bonusLevel: client.bonusLevel,
        loyaltyDiscount: client.loyaltyDiscount,
        preferredPriceType: client.preferredPriceType || null
      })
    }
  }, [client, showEditModal])

  useEffect(() => {
    if (userRole?.permissions?.deleteClient || context.user.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          client != null && <HeaderIconButton
            testID='client-delete-button'
            onPress={async () => {
              if (!isLoading) {
                try {
                  setIsLoading(true)
                  await deleteClient(db, client.id)
                  setIsLoading(false)
                  navigation.navigate('Home')
                } catch(error) {
                  Sentry.captureException(error)
                }
              }
            }}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
          />
        ),
      })
    }
  }, [isLoading, client])

  const loadClientSales = async (clientId, isFirstLoad = false, isCurrent = () => true) => {
    // Checked before the try: a return inside it would run the finally and clear the flag the
    // in-flight call owns.
    if (!isFirstLoad) {
      if (loadingMoreSalesRef.current) return
      loadingMoreSalesRef.current = true
    }
    try {
      if (isFirstLoad) {
        setIsLoadingSales(true)
        setSalesData([])
        setLastSalesDocument(null)
        setHasMoreSales(true)
        setSalesError(false)
      } else {
        setIsLoadingMoreSales(true)
      }

      const result = await getClientSalesPaginated(db, clientId, isFirstLoad ? null : lastSalesDocument, 15)
      if (!isCurrent()) return

      if (isFirstLoad) {
        setSalesData(result.docs)
      } else {
        setSalesData(prev => [...prev, ...result.docs])
      }
      
      setLastSalesDocument(result.lastDocument)
      setHasMoreSales(result.hasMore)
    } catch (error) {
      if (!isCurrent()) return
      console.error('Error loading client sales:', error)
      Sentry.captureException(error)
      setSalesError(true)
      // Stop paging: onEndReached must not retry a failing request in a loop.
      setHasMoreSales(false)
    } finally {
      if (!isFirstLoad) loadingMoreSalesRef.current = false
      if (isCurrent()) {
        setIsLoadingSales(false)
        setIsLoadingMoreSales(false)
      }
    }
  }

  const loadClientSummary = async (clientId, isCurrent) => {
    // Operations load on their own: a failure there must not hide the purchases (ATL-1472).
    getClientOperations(db, clientId)
      .then((clientOperationsData) => {
        if (!isCurrent()) return
        setClientOperations(clientOperationsData)
        setClientOperationsError(false)
      })
      .catch((error) => {
        if (!isCurrent()) return
        console.error('Error loading client operations:', error)
        Sentry.captureException(error)
        setClientOperationsError(true)
      })

    try {
      // ATL-1473: totals, top products and the purchase date range from ONE read of the client's
      // sales (it used to be three reads of the same whole list).
      const summary = await getClientSalesSummary(db, clientId, 15)
      if (!isCurrent()) return
      const { statistics, topProducts, earliestDate, latestDate } = summary

      // Calculate average frequency if we have date range
      let averageFrequency = null
      if (earliestDate && latestDate && statistics.salesCount > 1) {
        const daysDifference = differenceInCalendarDays(latestDate, earliestDate)
        averageFrequency = (daysDifference / statistics.salesCount).toFixed(1)
      }

      setPurchasesInfo({ 
        data: [], // We no longer load all sales here, only for the lazy loading list
        products: topProducts, 
        totalSum: statistics.totalRevenue, 
        profit: statistics.totalProfit,
        salesCount: statistics.salesCount,
        averageFrequency
      })
      setPurchasesInfoError(false)
    } catch (error) {
      if (!isCurrent()) return
      console.error('Error loading client data:', error)
      Sentry.captureException(error)
      setPurchasesInfoError(true)
    }
  }

  const cartItemNames = (cartItems) => {
    if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
      return ''
    }
    var names = ''
    for (const [index, value] of cartItems.entries()) {
      names = names + ', ' + value.name
    }
    return names.slice(2)
  }

  const renderItem = ({ item, index }) => {
    const date = format(item.saleDate.toDate(), 'd-M-y HH:mm')
    return (
      <Card
        style={styles.item}
        onPress={() => {
          navigation.navigate('SaleDetail', { detail: item })
        }}
      >
        <View style={styles.dateTimeView}>
          <Icon name="clock-outline" width={16} height={16} fill={theme['text-basic-color']} style={styles.icon}/>
          <Text
            style={styles.itemTitle}
            category="s2">
            {date}
          </Text>
          {item.paymentMethod?.id && ( // old sales don't have payment method id
            <FontAwesome5 name={item.paymentMethod.icon} size={16} color={theme['text-basic-color']} style={styles.icon}/>
          )}
          <Text
            style={styles.itemTitle}
            category="s2">
            {currencyFormatter.format(item.finalTotalCartPrice)}
          </Text>
        </View>
        <View style={styles.dateTimeView}>
          {item.purchaseMethod?.id ? (
            <FontAwesome5 name={item.purchaseMethod.icon} size={14} color={theme['text-basic-color']} style={styles.icon}/>
          ) : ( // to support old
            <>
              {item.purchaseMethod == 0 && (
                <FontAwesome5 name="building" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
              {item.purchaseMethod == 1 && (
                <FontAwesome5 name="walking" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
              {item.purchaseMethod == 2 && (<FontAwesome5 name="taxi" size={14} color={theme['text-basic-color']} style={styles.icon}/>)}
            </>
          )}
          <Text
            style={styles.itemTitle}
            category="s2">
            {storesDict?.[item.store]?.name}
          </Text>
          <Icon name="person-outline" width={16} height={16} fill={theme['text-basic-color']} style={styles.icon}/>
          <Text
            style={styles.itemTitle}
            category="s2">
            {usersDict[item.cashierUID]?.name}
          </Text>
        </View>
        {item.returnInfo && (
          <Text status="danger">
            {t('salesHistory.returnPresent')}
          </Text>
        )}
        <Text>
          {cartItemNames(item.cartItems)}
        </Text>

      </Card>
    )
  }

  const renderProduct = ({ item, index }) => (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
      <Text style={{ flex: 3 }}>{item.name}</Text>
      <Text style={{ flex: 1 }}>{item.checkQuantity}</Text>
      <Text style={{ flex: 1 }}>{item.saleQuantity}</Text>
      <Text style={{ flex: 1 }}>{item.saleAmount}</Text>
    </View>
  )

  const renderOperation = ({ item, index }) => {
    const date = format(item.date.toDate(), 'd-M-y HH:mm')
    return (
      <Card
        style={styles.item}
      >
        <View style={styles.dateTimeView}>
          <Icon name="clock-outline" width={16} height={16} fill={theme['text-basic-color']} style={styles.icon}/>
          <Text
            style={styles.itemTitle}
            category="s2">
            {date}
          </Text>
          <Icon name="person-outline" width={16} height={16} fill={theme['text-basic-color']} style={styles.icon}/>
          <Text
            style={styles.itemTitle}
            category="s2">
            {usersDict[item.userId]?.name}
          </Text>
        </View>
        { item.operationType === 'bonusChange' && (
          <Text style={{marginTop: 5}}>{t('clients.manualBonusChange') + ': ' + item.operationValue}</Text>
        )}
      </Card>
    )
  }

  const handleSave = async () => {
    try {
      setEditError(null)
      setIsLoading(true)
      
      // Validate phone number format — on the phone as it will be stored (ATL-1523): a phone carrying
      // an invisible character (U+202C closes most numbers copied from an iOS contact) is a valid
      // number, and refusing it made such a client impossible to edit.
      const phoneNumber = normalizePhoneText(editedClient.phoneNumber)
      const phoneRegex = /^\+?[0-9\s()-]+$/
      if (!phoneRegex.test(phoneNumber)) {
        throw new Error(t('clients.invalidPhoneNumber'))
      }

      const updates = {
        displayName: editedClient.displayName,
        phoneNumber,
        address: editedClient.address,
        bonusLevel: editedClient.bonusLevel,
        loyaltyDiscount: editedClient.loyaltyDiscount,
        preferredPriceType: editedClient.preferredPriceType
      }

      await updateClient(db, client.id, updates)

      // The card shows what was saved (ATL-1513: a local reassignment never reached the header).
      setClient((previous) => ({ ...previous, ...updates }))
      setShowEditModal(false)
      setIsLoading(false)
      toast.show(t('clients.updateSuccess'), {
        type: 'success',
        duration: 3000
      })
    } catch (error) {
      setEditError(error.message)
      setIsLoading(false)
      Sentry.captureException(error)
    }
  }

  return (
    <ScrollView style={{ padding: 20 }}>

      {client && (
        <View>
          <Text category="label" style={{ marginVertical: 10 }}>{'ID - ' + client.id}</Text>
          {clientLookupProblem && (
            <Text testID='client-lookup-problem' status='warning' style={{ marginBottom: 10 }}>
              {t(clientLookupProblem === 'missing' ? 'clients.clientNotFoundShowingSnapshot' : 'clients.clientRefreshFailed')}
            </Text>
          )}
          
          <Text>{t('clients.name') + ' - ' + client.displayName}</Text>
          <Text>{t('clients.phone') + ' - ' + client.phoneNumber}</Text>
          {client.address && <Text>{t('clients.address') + ' - ' + client.address}</Text>}
          <Text>{t('clients.bonusValue') + ' - ' + currencyFormatter.format(client.bonusValue)}</Text>
          { constants?.loyaltySettings.cashbackEnabled && 
            <Text>{t('clients.bonusLevel') + ' - ' + resolveBonusLevel(client) + '%'}</Text>
          }
          <Text>{t('clients.loyaltyDiscount') + ' - ' + ((client.loyaltyDiscount && client.loyaltyDiscount?.percentage != 0) ? client.loyaltyDiscount?.percentage ? client.loyaltyDiscount.percentage + '%' : currencyFormatter.format(client.loyaltyDiscount.amount || 0) : t('none'))}</Text>
          <View testID='client-edit-button'>
            <Button appearance='outline' disabled={!clientResolved} onPress={() => setShowEditModal(true)} style={{ marginTop: 10 }}>
              {t('clients.editClient')}
            </Button>
          </View>
          {purchasesInfo ? (
            <View>
              <Text>{t('clients.salesValue', {salesQuantity: purchasesInfo.salesCount, salesSum: currencyFormatter.format(purchasesInfo.totalSum)})}</Text>
              <Text>{t('clients.profitValue', {profit: currencyFormatter.format(purchasesInfo.profit)})}</Text>
              { purchasesInfo.averageFrequency && (
                <Text>{t('clients.averageSalesFrequency', {value: purchasesInfo.averageFrequency})}</Text>
              )}
            </View>
          ) : purchasesInfoError ? (
            <Text testID='client-purchases-info-error' status='danger' style={{ marginTop: 10 }}>{t('clients.purchasesLoadError')}</Text>
          ) : (
            <View testID='client-purchases-info-loading' style={{ alignItems: 'center' }}><Spinner/></View>
          )}
          <View>
            {constants?.loyaltySettings.cashbackEnabled && <Button appearance='outline' disabled={!clientResolved} onPress={() => setShowBonusChangeModal(true)} style={{ marginTop: 10 }}>
              {t('clients.addingBonuses')}
            </Button>}
            <WhatsAppButton testID='client-whatsapp' phone={client.phoneNumber} style={{marginTop: 10}}>
              {t('clients.writeOnWhatsapp')}
            </WhatsAppButton>
            <Text style={{ marginBottom: 5, marginTop: 10 }} category="label">
              {/* A failed later page stops paging, but the count is still a floor, not a total. */}
              {t('clients.purchases')} {salesData.length > 0 && `(${salesData.length}${hasMoreSales || salesError ? '+' : ''})`}
            </Text>
            {isLoadingSales ? (
              <View testID='client-purchases-loading' style={{ alignItems: 'center', marginVertical: 20 }}>
                <Spinner/>
              </View>
            ) : salesError && salesData.length === 0 ? (
              <Text testID='client-purchases-error' status='danger'>{t('clients.purchasesLoadError')}</Text>
            ) : (
              <FlatList
                data={salesData}
                renderItem={renderItem}
                keyExtractor={(item) => item.id}
                ListEmptyComponent={<Text testID='client-purchases-empty'>{t('clients.noClientPurchases')}</Text>}
                onEndReached={() => {
                  if (hasMoreSales && !isLoadingMoreSales && resolvedClientId) {
                    const generation = generationRef.current
                    void loadClientSales(resolvedClientId, false, () => generationRef.current === generation)
                  }
                }}
                onEndReachedThreshold={0.1}
                ListFooterComponent={() => {
                  if (isLoadingMoreSales) {
                    return (
                      <View style={{ alignItems: 'center', paddingVertical: 10 }}>
                        <Spinner size="small"/>
                      </View>
                    )
                  }
                  if (salesError) {
                    // A later page failed: the rows above are not the whole history.
                    return <Text testID='client-purchases-more-error' status='danger'>{t('clients.purchasesLoadError')}</Text>
                  }
                  return null
                }}
              />
            )}
            {purchasesInfo && (
              <>
                <Text style={{ marginBottom: 5, marginTop: 10 }} category="label">
                  {t('clients.topProducts')}
                </Text>
                <FlatList
                  data={purchasesInfo.products}
                  renderItem={renderProduct}
                />
              </>
            )}
          </View>
          { clientOperationsError && (
            <Text testID='client-operations-error' status='danger' style={{ marginTop: 10 }}>{t('clients.operationsLoadError')}</Text>
          )}
          { clientOperations && (
            <View>
              <Text style={{ marginBottom: 5, marginTop: 10 }} category="label">
              {t('clients.operations')}
              </Text>
              <FlatList
                data={clientOperations}
                renderItem={renderOperation}
              />
            </View>
          )}
           <Modal
            visible={showEditModal}
            backdropStyle={styles.backdrop}
            onBackdropPress={() => setShowEditModal(false)}
            style={styles.modal}>
            <Layout style={styles.modalContainer}>
              <Text category="h6" style={styles.modalTitle}>
                {t('clients.editClient')}
              </Text>
              <Text category='label' style={styles.label}>
                {t('clients.name')}
              </Text>
              <Input
                testID='client-edit-name-input'
                value={editedClient?.displayName}
                onChangeText={value => setEditedClient(prev => ({ ...prev, displayName: value }))}
                style={styles.input}
              />
              <Text category='label' style={styles.label}>
                {t('clients.phone')}
              </Text>
              <Input
                testID='client-edit-phone-input'
                value={editedClient?.phoneNumber}
                onChangeText={value => setEditedClient(prev => ({ ...prev, phoneNumber: value }))}
                style={styles.input}
              />
              <Text category='label' style={styles.label}>
                {t('clients.address')}
              </Text>
              <Input
                testID='client-edit-address-input'
                value={editedClient?.address || ''}
                onChangeText={value => setEditedClient(prev => ({ ...prev, address: value }))}
                style={styles.input}
              />
              { constants?.loyaltySettings.cashbackEnabled && (
                <>
                  <Text category='label' style={styles.label}>
                    {t('clients.bonusLevel')}
                  </Text>
                  {/* Numeric-only: this box is free text, and a typed '%' ("5%") used to reach
                      Firestore and make the cart's cashback evaluate to NaN. Rejecting the keystroke
                      keeps junk out at the source; resolveBonusLevel handles values already stored. */}
                  <Input
                    value={editedClient?.bonusLevel === null || editedClient?.bonusLevel === undefined ? '' : String(editedClient.bonusLevel)}
                    onChangeText={value => {
                      if (!isValidBonusLevelInput(value)) return
                      // Store the canonical dot form. A ru/kk numeric keypad emits a comma, and
                      // `Number('2,5')` is NaN — which the backend would coerce to NULL, silently
                      // dropping the cashback rate exactly as in ATL-713 (ATL-730).
                      setEditedClient(prev => ({ ...prev, bonusLevel: value.replace(',', '.') }))
                    }}
                    keyboardType='numeric'
                    style={styles.input}
                  />
                </>
              )}
              <Text category='label' style={styles.label}>
                {t('clients.loyaltyDiscount')}
              </Text>
              <PlatformDependantSelect
                data={discountOptions}
                selectedOption={editedClient?.loyaltyDiscount}
                onSelect={(itemValue, itemIndex) => {
                  setEditedClient(prev => ({ ...prev, loyaltyDiscount: itemValue }))
                }}
                style={styles.input}
              />
              
              {priceTypes && priceTypes.length > 0 && (
                <>
                  <Text category='label' style={styles.label}>
                    {t('clients.preferredPriceType') || 'Preferred Price Type'}
                  </Text>
                  <PlatformDependantSelect
                    data={[
                      { id: null, name: t('clients.none') || 'None', text: t('clients.none') || 'None' },
                      ...priceTypes.map(pt => ({ id: pt.id, name: pt.name, text: pt.name }))
                    ]}
                    selectedOption={editedClient?.preferredPriceType ? 
                      { ...priceTypes.find(pt => pt.id === editedClient.preferredPriceType), text: priceTypes.find(pt => pt.id === editedClient.preferredPriceType)?.name } : 
                      { id: null, name: t('clients.none') || 'None', text: t('clients.none') || 'None' }
                    }
                    onSelect={(itemValue) => {
                      setEditedClient(prev => ({ ...prev, preferredPriceType: itemValue.id }))
                    }}
                    style={styles.input}
                  />
                </>
              )}
              
              {editError && (
                <Text status="danger" style={styles.errorText}>{editError}</Text>
              )}
              
              <View style={styles.modalButtons}>
                <Button
                  status="basic"
                  onPress={() => setShowEditModal(false)}
                  style={styles.modalButton}>
                  {t('cancel')}
                </Button>
                <Button
                  testID='client-edit-save'
                  onPress={handleSave}
                  style={styles.modalButton}
                  disabled={isLoading}>
                  {isLoading ? t('saving') : t('save')}
                </Button>
              </View>
            </Layout>
          </Modal>
          <Modal backdropStyle={styles.backdrop} visible={showBonusChangeModal}
                     onBackdropPress={() => setShowBonusChangeModal(false)} style={styles.modal}>
            <Layout style={styles.modalContainer}>
              <Text style={{ marginBottom: 10, marginTop: 10 }} category="label">
                {t('clients.addBonuses')}
              </Text>
              <BonusValueChange db={db} client={client}
                                onBonusUpdate={(newBonusValue) => {
                                  if (typeof newBonusValue === 'number') {
                                    setClient((previous) => ({ ...previous, bonusValue: newBonusValue }))
                                  }
                                  setShowBonusChangeModal(false)
                                }}/>

              <Button  status='basic' onPress={() => setShowBonusChangeModal(false)}
                      style={{ marginTop: 10 }}>
                {t('cancel')}
              </Button>
            </Layout>
          </Modal>
        </View>
      )}
      {!client && <View style={{ alignItems: 'center', marginTop: 10 }}><Spinner/></View>}
      <View style={{ height: 40 }}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modal: {
    width: '90%',
    maxWidth: 420,
  },
  modalContainer: {
    borderRadius: 20,
    padding: 24,
  },
  modalTitle: {
    marginBottom: 20,
    textAlign: 'center',
  },
  input: {
    marginBottom: 16,
  },
  label: {
    marginBottom: 5,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  modalButton: {
    marginLeft: 12,
  },
  errorText: {
    marginTop: 8,
    textAlign: 'center',
  },
})
